import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import ArticleContent from '@/components/feature/ArticleContent';
import {
  formatArticleDate,
  getArticleBySlug,
  listPublishedArticles,
  listPublishedArticlesForCommerce,
  readingMinutes,
  tagList,
  type BlogArticle,
} from '@/lib/blog';

// ─────────────────────────────────────────────────────────────
// Blog de la boutique (site public du marchand).
// Les articles publiés depuis /dashboard/blog sont affichés ici :
// la page d'accueil, /blog et /blog/:slug fonctionnent enfin.
// ─────────────────────────────────────────────────────────────

const FALLBACK_COVER =
  'https://readdy.ai/api/search-image?query=Warm%20editorial%20desk%20scene%20with%20notebook%2C%20coffee%20and%20natural%20light%2C%20soft%20beige%20tones%2C%20minimal%20modern%20photography&width=1200&height=700&seq=tenant-blog-cover-2026&orientation=landscape';

function useTenantArticles(limit = 60) {
  const { tenant, isTenant } = useTenant();
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        let rows: BlogArticle[] = [];
        if (isTenant && tenant?.id) {
          rows = await listPublishedArticlesForCommerce(tenant.id, limit);
        }
        if (rows.length === 0) rows = await listPublishedArticles(limit);
        if (alive) setArticles(rows);
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : 'Impossible de charger le blog.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isTenant, tenant?.id, limit]);

  return { articles, loading, error };
}

export function TenantBlogPage() {
  const { articles, loading, error } = useTenantArticles();
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState('all');

  const tags = useMemo(() => {
    const set = new Set<string>();
    articles.forEach((a) => tagList(a.tags).forEach((t) => set.add(t)));
    return Array.from(set).slice(0, 10);
  }, [articles]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return articles.filter((a) => {
      if (tag !== 'all' && !tagList(a.tags).includes(tag)) return false;
      if (!q) return true;
      return `${a.title} ${a.excerpt || ''} ${a.tags || ''}`.toLowerCase().includes(q);
    });
  }, [articles, search, tag]);

  return (
    <div className="min-h-[60vh] bg-background-50">
      <header className="py-12 md:py-16 px-4 md:px-6 bg-background-100/60">
        <div className="max-w-6xl mx-auto">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold">
            <i className="ri-article-line"></i>Blog
          </span>
          <h1 className="mt-4 text-2xl md:text-4xl font-bold font-heading text-foreground-950 leading-tight">
            Nos articles et conseils
          </h1>
          <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-2xl">
            Nouveautés, guides pratiques et coulisses de la maison.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1 min-w-0">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un article…"
                className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
            </div>
            {tags.length > 0 && (
              <select
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                className="px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-700 cursor-pointer focus:outline-none"
              >
                <option value="all">Toutes les catégories</option>
                {tags.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
          </div>
        </div>
      </header>

      <main className="px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
            </div>
          ) : error ? (
            <div className="py-16 text-center">
              <i className="ri-error-warning-line text-3xl text-accent-600"></i>
              <p className="mt-3 text-sm text-foreground-600">{error}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center bg-background-50 border border-background-200/70 rounded-lg">
              <i className="ri-article-line text-3xl text-foreground-300"></i>
              <p className="mt-3 text-sm text-foreground-600">Aucun article publié pour le moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {filtered.map((a) => (
                <Link
                  key={a.id}
                  to={`/blog/${a.slug || a.id}`}
                  className="group flex flex-col bg-background-50 border border-background-200/70 rounded-lg overflow-hidden no-underline hover:border-primary-200 transition-colors"
                >
                  <div className="w-full aspect-[16/10] overflow-hidden bg-background-100">
                    <img
                      src={a.cover_image || FALLBACK_COVER}
                      alt={a.title}
                      title={`${a.title} — blog`}
                      className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3.5 flex-1 flex flex-col">
                    {tagList(a.tags).length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-1.5">
                        {tagList(a.tags).slice(0, 2).map((t) => (
                          <span key={t} className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[10px] font-semibold whitespace-nowrap">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    <h2 className="text-sm font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                      {a.title}
                    </h2>
                    {a.excerpt && (
                      <p className="mt-1.5 text-[11px] text-foreground-500 line-clamp-2 flex-1">{a.excerpt}</p>
                    )}
                    <div className="mt-3 pt-2.5 border-t border-background-200/60 flex items-center justify-between text-[10px] text-foreground-500">
                      <span>{formatArticleDate(a.published_at || a.created_at)}</span>
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i>{readingMinutes(a.content)} min</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export function TenantBlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [related, setRelated] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!slug) return;
      setLoading(true);
      try {
        const found = await getArticleBySlug(slug);
        if (!alive) return;
        setArticle(found);
        if (found) {
          const all = await listPublishedArticles(8);
          if (alive) setRelated(all.filter((a) => a.id !== found.id).slice(0, 3));
        }
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : 'Impossible de charger cet article.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <i className="ri-error-warning-line text-3xl text-accent-600"></i>
        <p className="mt-3 text-sm text-foreground-600">{error}</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <i className="ri-file-search-line text-3xl text-foreground-300"></i>
        <h1 className="mt-4 text-xl font-bold font-heading text-foreground-950">Article introuvable</h1>
        <p className="mt-2 text-sm text-foreground-600">Cet article n'existe pas ou n'est plus publié.</p>
        <Link to="/blog" className="inline-flex items-center gap-1.5 mt-5 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline hover:bg-primary-600 transition-colors">
          <i className="ri-arrow-left-line"></i>Retour au blog
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] bg-background-50">
      <div className="w-full aspect-[21/9] max-h-[380px] overflow-hidden bg-background-100">
        <img
          src={article.cover_image || FALLBACK_COVER}
          alt={article.title}
          title={`${article.title} — blog`}
          className="w-full h-full object-cover object-top"
        />
      </div>

      <article className="max-w-3xl mx-auto px-4 md:px-6 -mt-12 relative pb-12">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-9">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-xs text-foreground-500 hover:text-foreground-800 no-underline mb-4">
            <i className="ri-arrow-left-line"></i>Blog
          </Link>

          {tagList(article.tags).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {tagList(article.tags).map((t) => (
                <span key={t} className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-900 text-[11px] font-semibold">{t}</span>
              ))}
            </div>
          )}

          <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 leading-tight">{article.title}</h1>
          {article.excerpt && (
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
          )}

          <div className="mt-5 pb-5 border-b border-background-200/70 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{formatArticleDate(article.published_at || article.created_at)}</span>
            <span className="flex items-center gap-1"><i className="ri-time-line"></i>{readingMinutes(article.content)} min de lecture</span>
          </div>

          <div className="mt-6">
            <ArticleContent content={article.content} />
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 md:px-6 pb-14">
          <h2 className="text-lg font-bold font-heading text-foreground-950 mb-5">À lire ensuite</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {related.map((a) => (
              <Link
                key={a.id}
                to={`/blog/${a.slug || a.id}`}
                className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden no-underline hover:border-primary-200 transition-colors"
              >
                <div className="w-full aspect-[16/10] overflow-hidden bg-background-100">
                  <img src={a.cover_image || FALLBACK_COVER} alt={a.title} title={a.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-3.5">
                  <h3 className="text-sm font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">{a.title}</h3>
                  <p className="mt-1 text-[11px] text-foreground-500">{formatArticleDate(a.published_at || a.created_at)}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}